(() => {
  const DEBOUNCE_DELAY = 320;
  const cache = new Map();

  const normalizeQuery = (value) =>
    value
      .normalize("NFKC")
      .replace(/[\u0640\u064B-\u065F\u0670\u06D6-\u06ED]/g, "")
      .replace(/\*/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const buildPrefixQuery = (query) => {
    const words = query.split(" ");
    words[words.length - 1] = `${words[words.length - 1]}*`;
    return words.join(" ");
  };

  const isSkuQuery = (query) =>
    /\d/.test(query) && /^[a-z0-9._/-]+$/i.test(query);

  const normalizeSku = (value) =>
    String(value || "")
      .normalize("NFKC")
      .trim()
      .toLocaleUpperCase("en-US");

  const normalizeExactTitle = (value) =>
    String(value || "")
      .normalize("NFKC")
      .replace(/[\u0640\u064B-\u065F\u0670\u06D6-\u06ED]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLocaleLowerCase(document.documentElement.lang || undefined);

  const readProductTitle = (item) =>
    item.querySelector(".tanatel-live-search__title")?.textContent || "";

  const findExactItem = (section, query, skuMode) => {
    const items = Array.from(section.querySelectorAll("[data-live-search-item]"));

    if (skuMode) {
      const wantedSku = normalizeSku(query);
      return (
        items.find((item) =>
          readProductSkus(item).some((sku) => normalizeSku(sku) === wantedSku)
        ) || null
      );
    }

    const wantedTitle = normalizeExactTitle(query);
    return (
      items.find(
        (item) => normalizeExactTitle(readProductTitle(item)) === wantedTitle
      ) || null
    );
  };

  const readProductSkus = (item) => {
    try {
      const skus = JSON.parse(item.dataset.productSkus || "[]");
      return Array.isArray(skus) ? skus : [];
    } catch (error) {
      return [];
    }
  };

  const setExactSkuEmptyState = (section, query) => {
    const viewport = section.querySelector("[data-live-search-viewport]") || section;
    const message = document.createElement("p");
    message.className = "tanatel-live-search__empty";
    message.textContent = document.documentElement.lang?.toLowerCase().startsWith("ar")
      ? `غير متوفر «${query}»`
      : `No product has the exact SKU “${query}”`;
    viewport.replaceChildren(message);
  };

  const filterReturnedResults = (section, query, skuMode) => {
    const items = Array.from(section.querySelectorAll("[data-live-search-item]"));

    if (!skuMode) {
      items.slice(8).forEach((item) => item.remove());
      return;
    }

    const exactSku = normalizeSku(query);
    section.querySelector(".tanatel-live-search__suggestions")?.remove();
    section.querySelector(".tanatel-live-search__footer")?.remove();

    items.forEach((item) => {
      const hasExactSku = readProductSkus(item).some(
        (sku) => normalizeSku(sku) === exactSku
      );
      if (!hasExactSku) item.remove();
    });

    const exactItems = Array.from(section.querySelectorAll("[data-live-search-item]"));
    exactItems.slice(8).forEach((item) => item.remove());

    if (exactItems.length === 0) {
      setExactSkuEmptyState(section, query);
    }
  };

  const initLiveSearch = (root) => {
    if (root.dataset.liveSearchReady === "true") return;

    const form = root.querySelector("form");
    const input = root.querySelector("[data-search-input]");
    const popup = root.closest("[data-search-popup]");
    const results = popup?.querySelector("[data-live-search-results]");
    const popular = popup?.querySelector("[data-popular-searches]");
    const spinner = root.querySelector("[data-live-search-spinner]");
    const searchUrl = root.dataset.searchUrl;
    const minCharacters = Number(root.dataset.minCharacters) || 3;

    if (!form || !input || !results || !searchUrl) return;

    root.dataset.liveSearchReady = "true";
    let debounceTimer;
    let activeController;
    let requestSequence = 0;
    let dragState = null;
    let suppressClickUntil = 0;

    const setLoading = (loading) => {
      if (spinner) spinner.hidden = !loading;
      form.classList.toggle("is-loading", loading);
    };

    const closeResults = () => {
      activeController?.abort();
      activeController = null;
      setLoading(false);
      results.replaceChildren();
      results.scrollTop = 0;
      results.classList.remove("is-mouse-dragging");
      results.classList.add("m:hidden");
      popular?.classList.remove("m:hidden");
      input.setAttribute("aria-expanded", "false");
    };

    const showResults = (markup) => {
      results.innerHTML = markup;
      results.scrollTop = 0;
      results.classList.remove("m:hidden");
      popular?.classList.add("m:hidden");
      input.setAttribute("aria-expanded", "true");
    };

    const fetchResults = async (query) => {
      const skuMode = isSkuQuery(query);
      const cacheKey = `${document.documentElement.lang || "default"}:${skuMode ? "sku" : "text"}:${query.toLowerCase()}`;
      if (cache.has(cacheKey)) {
        showResults(cache.get(cacheKey));
        return;
      }

      activeController?.abort();
      activeController = new AbortController();
      const currentRequest = ++requestSequence;
      setLoading(true);

      const url = new URL(searchUrl, window.location.origin);
      url.searchParams.set("q", skuMode ? query : buildPrefixQuery(query));
      url.searchParams.set("type", "product");
      if (skuMode) {
        url.searchParams.delete("options[prefix]");
        url.searchParams.set("options[fields]", "variants.sku");
      } else {
        url.searchParams.set("options[prefix]", "last");
        url.searchParams.set("options[fields]", "title");
      }
      url.searchParams.set("options[unavailable_products]", "last");
      url.searchParams.set("section_id", "tanatel-live-search");

      try {
        const response = await fetch(url.toString(), {
          headers: { Accept: "text/html" },
          signal: activeController.signal,
        });

        if (!response.ok) throw new Error(`Live search failed: ${response.status}`);

        const html = await response.text();
        const documentResult = new DOMParser().parseFromString(html, "text/html");
        const section = documentResult.querySelector("#shopify-section-tanatel-live-search");

        if (!section) throw new Error("Live search section was not returned");
        if (currentRequest !== requestSequence) return;

        filterReturnedResults(section, query, skuMode);
        const markup = section.innerHTML.trim();
        cache.set(cacheKey, markup);
        showResults(markup);
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error(error);
        results.innerHTML =
          document.documentElement.lang?.toLowerCase().startsWith("ar")
            ? '<p class="tanatel-live-search__error">تعذر تحميل النتائج، حاول مرة أخرى.</p>'
            : '<p class="tanatel-live-search__error">Results could not be loaded. Please try again.</p>';
        showResults(results.innerHTML);
      } finally {
        if (currentRequest === requestSequence) setLoading(false);
      }
    };

    const showExactSubmitEmptyState = (query, skuMode) => {
      results.replaceChildren();

      const message = document.createElement("p");
      message.className = "tanatel-live-search__empty";

      const isArabic =
        document.documentElement.lang?.toLowerCase().startsWith("ar");

      message.textContent = isArabic
        ? skuMode
          ? `لا يوجد منتج برقم مطابق تمامًا «${query}»`
          : `لا يوجد منتج باسم مطابق تمامًا «${query}»`
        : skuMode
          ? `No product has the exact number “${query}”`
          : `No product has the exact name “${query}”`;

      results.appendChild(message);
      results.classList.remove("m:hidden");
      popular?.classList.add("m:hidden");
      input.setAttribute("aria-expanded", "true");
    };

    const submitExactSearch = async (query) => {
      const skuMode = isSkuQuery(query);

      const currentExact = findExactItem(results, query, skuMode);
      if (currentExact?.href) {
        window.location.assign(currentExact.href);
        return;
      }

      activeController?.abort();
      activeController = new AbortController();
      const currentRequest = ++requestSequence;
      setLoading(true);

      const url = new URL(searchUrl, window.location.origin);
      url.searchParams.set("q", query);
      url.searchParams.set("type", "product");
      url.searchParams.delete("options[prefix]");
      url.searchParams.set(
        "options[fields]",
        skuMode ? "variants.sku" : "title"
      );
      url.searchParams.set("options[unavailable_products]", "last");
      url.searchParams.set("section_id", "tanatel-live-search");

      try {
        const response = await fetch(url.toString(), {
          headers: { Accept: "text/html" },
          signal: activeController.signal,
        });

        if (!response.ok) {
          throw new Error(`Exact search failed: ${response.status}`);
        }

        const html = await response.text();
        const documentResult = new DOMParser().parseFromString(
          html,
          "text/html"
        );
        const section = documentResult.querySelector(
          "#shopify-section-tanatel-live-search"
        );

        if (!section) throw new Error("Exact search section was not returned");
        if (currentRequest !== requestSequence) return;

        const exactItem = findExactItem(section, query, skuMode);

        if (exactItem?.href) {
          window.location.assign(exactItem.href);
          return;
        }

        showExactSubmitEmptyState(query, skuMode);
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error(error);
        showExactSubmitEmptyState(query, skuMode);
      } finally {
        if (currentRequest === requestSequence) setLoading(false);
      }
    };

    input.addEventListener("input", () => {
      window.clearTimeout(debounceTimer);
      const query = normalizeQuery(input.value);

      if (query.length < minCharacters) {
        closeResults();
        return;
      }

      debounceTimer = window.setTimeout(() => fetchResults(query), DEBOUNCE_DELAY);
    });

    form.addEventListener("submit", (event) => {
      const query = normalizeQuery(input.value);
      if (!query) return;

      event.preventDefault();
      event.stopPropagation();
      window.clearTimeout(debounceTimer);
      submitExactSearch(query);
    });

    results.addEventListener(
      "click",
      (event) => {
        if (Date.now() > suppressClickUntil) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    results.addEventListener("click", (event) => {
      const viewAllLink = event.target.closest("[data-live-search-view-all]");
      if (viewAllLink) {
        const destination = viewAllLink.href;
        if (!destination) return;

        event.preventDefault();
        event.stopPropagation();
        window.location.assign(destination);
        return;
      }

      const suggestion = event.target.closest("[data-live-search-query]");
      if (!suggestion) return;

      const suggestedQuery = normalizeQuery(suggestion.dataset.liveSearchQuery || "");
      if (!suggestedQuery) return;

      input.value = suggestedQuery;
      input.focus();
      fetchResults(suggestedQuery);
    });

    results.addEventListener("pointerup", (event) => {
      if (event.pointerType === "mouse") return;

      const viewAllLink = event.target.closest("[data-live-search-view-all]");
      if (!viewAllLink || !viewAllLink.href) return;

      event.preventDefault();
      event.stopPropagation();
      window.location.assign(viewAllLink.href);
    });

    const finishMouseDrag = (event) => {
      if (!dragState || (event && event.pointerId !== dragState.pointerId)) return;

      const moved = dragState.moved;
      const pointerId = dragState.pointerId;
      dragState = null;
      results.classList.remove("is-mouse-dragging");

      if (results.hasPointerCapture?.(pointerId)) {
        results.releasePointerCapture(pointerId);
      }

      if (moved) {
        suppressClickUntil = Date.now() + 350;
      }
    };

    results.addEventListener("pointerdown", (event) => {
      if (
        event.pointerType !== "mouse" ||
        event.button !== 0 ||
        results.scrollHeight <= results.clientHeight
      ) {
        return;
      }

      dragState = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startScrollTop: results.scrollTop,
        moved: false,
      };
      results.setPointerCapture?.(event.pointerId);
    });

    results.addEventListener("pointermove", (event) => {
      if (!dragState || event.pointerId !== dragState.pointerId) return;

      const distance = event.clientY - dragState.startY;
      if (!dragState.moved && Math.abs(distance) < 4) return;

      dragState.moved = true;
      results.classList.add("is-mouse-dragging");
      results.scrollTop = dragState.startScrollTop - distance;
      event.preventDefault();
    });

    results.addEventListener("pointerup", finishMouseDrag);
    results.addEventListener("pointercancel", finishMouseDrag);
    results.addEventListener("lostpointercapture", finishMouseDrag);
    results.addEventListener("dragstart", (event) => {
      if (dragState) event.preventDefault();
    });

    popup?.querySelector("[data-close-search]")?.addEventListener("click", closeResults);
    form.addEventListener("reset", closeResults);
  };

  const boot = () => {
    document.querySelectorAll("[data-tanatel-live-search]").forEach(initLiveSearch);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();
